import { useWallet } from '@solana/wallet-adapter-react'
import { useEffect, useState } from 'react'
import { EmptyBlock, ErrorBlock, Panel, Spinner } from '~/components/ui'
import {
    cancelListing,
    editPrice,
    fetchMyListings,
    ListingState,
    requestChallenge,
    TradeError,
    TradeListing,
} from '../api/trade'
import { ConnectWallet } from './ConnectWallet'

const encodeSignature = (bytes: Uint8Array): string => {
    // The server parses this with solders' `Signature.from_string`, which wants
    // base58 -- the same encoding a wallet shows the user.
    const ALPHABET = '123456789ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnopqrstuvwxyz'
    let digits = [0]
    for (const byte of bytes) {
        let carry = byte
        for (let i = 0; i < digits.length; i += 1) {
            carry += digits[i] << 8
            digits[i] = carry % 58
            carry = (carry / 58) | 0
        }
        while (carry > 0) {
            digits.push(carry % 58)
            carry = (carry / 58) | 0
        }
    }
    let leading = ''
    for (const byte of bytes) {
        if (byte !== 0) break
        leading += '1'
    }
    return leading + digits.reverse().map((d) => ALPHABET[d]).join('')
}

/** What a listing that cannot be changed is doing, so a missing button has an
 *  explanation. Reprice and Take down exist only for the two states the server
 *  accepts them in; offering them elsewhere just produced a refusal. */
const WAITING_TEXT: Record<number, string> = {
    [ListingState.AWAITING_ESCROW]: 'Waiting for the transfer to confirm',
    [ListingState.CANCELLING]: 'On its way back to your wallet',
    [ListingState.STUCK]: 'Our team is looking at this',
}

const canChange = (state: number) =>
    state === ListingState.ACTIVE || state === ListingState.PENDING_REVIEW

export const MyListings = ({ onChanged }: { onChanged: () => void }) => {
    const { signMessage, publicKey } = useWallet()
    const [listings, setListings] = useState<TradeListing[] | null>(null)
    const [loadError, setLoadError] = useState(false)
    const [busyId, setBusyId] = useState<number | null>(null)
    const [error, setError] = useState<string | null>(null)
    const [editing, setEditing] = useState<number | null>(null)
    const [newPrice, setNewPrice] = useState('')

    const load = () => {
        setLoadError(false)
        setListings(null)
        fetchMyListings()
            .then((data) => setListings(data.results))
            .catch(() => setLoadError(true))
    }

    useEffect(load, [])

    /** Ask for a challenge, have the wallet sign the exact message the server
     *  returned, and hand both back. The message is never rebuilt here: signing
     *  something the server did not issue is how a signature ends up covering
     *  the wrong thing. */
    const sign = async (listingId: number, action: 'cancel' | 'edit') => {
        if (!signMessage) throw new TradeError('This wallet cannot sign messages.', 'no_signer')
        const challenge = await requestChallenge(listingId, action)
        const signed = await signMessage(new TextEncoder().encode(challenge.message))
        return { nonce: challenge.nonce, signature: encodeSignature(signed) }
    }

    const cancel = async (listing: TradeListing) => {
        setBusyId(listing.id)
        setError(null)
        try {
            const { nonce, signature } = await sign(listing.id, 'cancel')
            await cancelListing(listing.id, nonce, signature)
            load()
            onChanged()
        } catch (e) {
            setError(e instanceof TradeError ? e.message : 'The wallet did not sign. Nothing changed.')
        } finally {
            setBusyId(null)
        }
    }

    const savePrice = async (listing: TradeListing) => {
        setBusyId(listing.id)
        setError(null)
        try {
            const { nonce, signature } = await sign(listing.id, 'edit')
            await editPrice(listing.id, newPrice, nonce, signature)
            setEditing(null)
            setNewPrice('')
            load()
            onChanged()
        } catch (e) {
            setError(e instanceof TradeError ? e.message : 'The wallet did not sign. Nothing changed.')
        } finally {
            setBusyId(null)
        }
    }

    if (loadError) return <ErrorBlock onRetry={load} />
    if (listings === null)
        return (
            <div className='flex justify-center py-16'>
                <Spinner visible={true} />
            </div>
        )
    if (listings.length === 0)
        return <EmptyBlock title='No active listings'>Anything you list will appear here.</EmptyBlock>

    return (
        <div className='flex flex-col gap-3'>
            <ConnectWallet prompt='Connect the wallet you listed from to cancel or reprice.' />
            {/* Cancelling and repricing are authorised by a signed message, and
                hardware wallets often cannot sign one. Say so up front rather
                than letting the first click fail. */}
            {publicKey && !signMessage && (
                <Panel className='p-4 text-sm text-[#FFC53D]' border='rgba(255,197,61,0.35)'>
                    This wallet cannot sign messages, which taking down or repricing a listing needs.
                    Some wallets, hardware wallets in particular, do not support it. Try another wallet,
                    or contact support and we will help.
                </Panel>
            )}
            {error && <p className='text-xs text-[#FF6B6B]'>{error}</p>}

            {listings.map((listing) => (
                <Panel key={listing.id} className='flex flex-col gap-3 p-3 sm:flex-row sm:items-center'>
                    <div className='h-16 w-16 shrink-0 overflow-hidden bg-white/[0.03]'>
                        {listing.nft.image_url && (
                            <img
                                src={listing.nft.image_url}
                                alt={listing.nft.name}
                                className='h-full w-full object-contain'
                            />
                        )}
                    </div>

                    <div className='min-w-0 flex-1'>
                        <p className='truncate text-sm font-bold text-white'>{listing.nft.name}</p>
                        <p className='text-[11px] uppercase tracking-widest text-white/40'>
                            {listing.state_label} · ${listing.price}
                        </p>
                        {listing.review_reason && (
                            <p className='mt-1 text-xs text-[#FFC53D]'>{listing.review_reason}</p>
                        )}
                    </div>

                    {!canChange(listing.state) ? (
                        WAITING_TEXT[listing.state] && (
                            <p className='text-xs text-white/50'>{WAITING_TEXT[listing.state]}</p>
                        )
                    ) : editing === listing.id ? (
                        <div className='flex items-center gap-2'>
                            <input
                                value={newPrice}
                                onChange={(e) => setNewPrice(e.target.value)}
                                inputMode='decimal'
                                placeholder='New price'
                                className='w-28 border border-white/15 bg-black/40 px-2 py-2 text-sm font-bold text-white outline-none focus:border-[#03CEFF]'
                            />
                            <button
                                onClick={() => savePrice(listing)}
                                disabled={busyId === listing.id || newPrice === ''}
                                className='min-h-[2.25rem] bg-[#03CEFF] px-3 text-xs font-black uppercase tracking-widest text-black disabled:opacity-40'
                            >
                                Save
                            </button>
                            <button
                                onClick={() => setEditing(null)}
                                className='min-h-[2.25rem] border border-white/15 px-3 text-xs font-black uppercase tracking-widest text-white/70'
                            >
                                Cancel
                            </button>
                        </div>
                    ) : (
                        <div className='flex items-center gap-2'>
                            <button
                                onClick={() => {
                                    setEditing(listing.id)
                                    setNewPrice(listing.price)
                                }}
                                disabled={busyId === listing.id}
                                className='min-h-[2.25rem] border border-white/15 px-3 text-xs font-black uppercase tracking-widest text-white/70 hover:border-white/30 disabled:opacity-40'
                            >
                                Reprice
                            </button>
                            <button
                                onClick={() => cancel(listing)}
                                disabled={busyId === listing.id}
                                className='min-h-[2.25rem] border border-white/15 px-3 text-xs font-black uppercase tracking-widest text-white/70 hover:border-white/30 disabled:opacity-40'
                            >
                                {busyId === listing.id ? 'Signing…' : 'Take down'}
                            </button>
                        </div>
                    )}
                </Panel>
            ))}
        </div>
    )
}
